import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    DialogActions,
    MenuItem,
    Autocomplete,
    CircularProgress,
} from "@mui/material";
import { useDocumentsType } from "../../hooks/useDocumentsType";

// Esquema de validación
const schema = yup.object().shape({
    doctipid: yup.number().required("Debe seleccionar un tipo de documento"),
    reqent: yup
        .string()
        .oneOf(["CONDUCTOR", "VEHICULO"], "Debe seleccionar una entidad válida")
        .required("Campo obligatorio"),
});

const ProjectDocumentsForm = ({ open, documents, handleClose, onSubmit, defaultValues = {} }) => {
    const isEditing = Boolean(defaultValues?.prodocreqid);
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            doctipid: "",
            reqent: "CONDUCTOR",
            ...defaultValues,
        },
    });

    const { documentsType = [], isLoading: loadingDocumentsType } = useDocumentsType();

    // Resetear cuando se abre o cambian los datos
    useEffect(() => {
        if (open) {
            // Esperar a que los tipos estén cargados
            if (documentsType.length === 0) return;

            // Validar si el valor actual existe en la lista
            const currentId = defaultValues.doctipid;
            const isValid = documentsType.some((doc) => doc.doctipid === currentId);

            reset({
                ...defaultValues,
                doctipid: isValid ? currentId : "",
                reqent: defaultValues.reqent || "CONDUCTOR",
            });
        }
    }, [open, defaultValues, reset, documentsType]);

    // Opciones disponibles: excluir tipos ya usados por otros documentos, pero mantener el actual si estamos editando
    const availableOptions = useMemo(() => {
        if (!documentsType.length) return [];

        // IDs de documentos ya seleccionados (excepto el que estamos editando)
        const usedIds = new Set(
            documents
                .filter((doc) => !isEditing || doc.prodocreqid !== defaultValues.prodocreqid)
                .map((doc) => doc.doctipid)
        );

        return documentsType.filter((doc) => !usedIds.has(doc.doctipid));
    }, [documentsType, documents, isEditing, defaultValues.prodocreqid]);

    // Obtener el objeto del tipo seleccionado (para mostrar en Autocomplete)
    const selectedDoc = useMemo(() => {
        const currentId = watch("doctipid");
        if (!currentId) return null;
        return documentsType.find((doc) => doc.doctipid === currentId) || null;
    }, [watch("doctipid"), documentsType]);

    // Manejar cambio en Autocomplete
    const handleDocChange = (event, newValue) => {
        setValue("doctipid", newValue ? newValue.doctipid : "", { shouldValidate: true });
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {isEditing ? "Editar Documento Requerido" : "Agregar Documento Requerido"}
            </DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent>
                    {/* Tipo de Documento con Autocomplete */}
                    <Controller
                        name="doctipid"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                options={availableOptions}
                                loading={loadingDocumentsType}
                                getOptionLabel={(option) => option.doctip || ""}
                                isOptionEqualToValue={(option, value) => option.doctipid === value.doctipid}
                                value={selectedDoc}
                                onChange={handleDocChange}
                                disabled={loadingDocumentsType || availableOptions.length === 0}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Tipo de Documento *"
                                        margin="normal"
                                        error={!!errors.doctipid}
                                        helperText={errors.doctipid?.message}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingDocumentsType ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.doctipid}>
                                        {option.doctip}
                                    </li>
                                )}
                                noOptionsText="No hay tipos de documentos disponibles"
                                sx={{ mt: 1 }}
                            />
                        )}
                    />

                    {/* Entidad Requerida */}
                    <Controller
                        name="reqent"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                select
                                label="Entidad Requerida *"
                                fullWidth
                                margin="normal"
                                error={!!errors.reqent}
                                helperText={errors.reqent?.message}
                                value={field.value || "CONDUCTOR"}
                            >
                                <MenuItem value="CONDUCTOR">Conductor</MenuItem>
                                <MenuItem value="VEHICULO">Vehículo</MenuItem>
                            </TextField>
                        )}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loadingDocumentsType || availableOptions.length === 0}
                    >
                        {isEditing ? "Actualizar" : "Guardar"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ProjectDocumentsForm;